import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [missingToken, setMissingToken] = useState(false);

  useEffect(() => {
    if (!token) setMissingToken(true);
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      // Auto sign-in: backend returns a fresh JWT
      if (data?.token) {
        localStorage.setItem("aip_token", data.token);
        await refresh();
      }
      toast.success("Password updated — you're signed in");
      navigate("/dashboard");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not reset password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="reset-password-page"
      className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-6 py-16"
    >
      <div className="flex flex-col items-center text-center">
        <img
          src={H_LOGO_URL}
          alt="Hireloop PRO"
          className="h-16 w-16 object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
        <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          Choose a new password
        </div>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Reset password
        </h1>
        <p className="mt-3 max-w-sm text-sm text-black">
          Pick a strong password with at least 6 characters. You'll be signed in automatically once it's saved.
        </p>
      </div>

      {missingToken ? (
        <div
          data-testid="reset-password-missing-token"
          className="mt-10 border border-amber-200 bg-amber-50 px-6 py-6 text-center"
        >
          <p className="text-sm text-amber-900">
            This reset link is missing its token. Please request a new one from the{" "}
            <Link to="/forgot-password" className="font-medium underline">
              forgot password page
            </Link>
            .
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              New password
            </Label>
            <Input
              id="password"
              data-testid="reset-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-sm border-slate-300"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              Confirm new password
            </Label>
            <Input
              id="confirm"
              data-testid="reset-password-confirm-input"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 rounded-sm border-slate-300"
            />
          </div>
          <Button
            type="submit"
            data-testid="reset-password-submit-btn"
            disabled={loading}
            className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="mr-2 h-4 w-4" />
            )}
            Update password &amp; sign in
          </Button>
        </form>
      )}

      <p className="mt-8 text-center text-sm text-slate-500">
        Back to{" "}
        <Link
          to="/login"
          data-testid="reset-password-to-login-link"
          className="font-medium text-blue-700 hover:underline"
        >
          sign in
        </Link>
      </p>
    </main>
  );
}
